"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  useTheme,
  alpha,
} from "@mui/material";
import Head from "next/head";
import PopoverForm from "@/components/PopoverForm";

export default function AgenciesIndustry() {
  const theme = useTheme();

  const benefits = [
    {
      title: "Seamless White-Label Delivery",
      desc: "We align with your brand and workflows, backed by NDAs for client trust.",
    },
    {
      title: "Scalable Development",
      desc: "From websites to automation, we scale up or down based on your client needs.",
    },
    {
      title: "Dedicated Partner",
      desc: "Think of us as your extended tech team, ready for overflow or specialized projects.",
    },
  ];

  const processSteps = [
    {
      step: "01",
      title: "Onboarding & NDA",
      desc: "We sign NDAs and adapt to your tools and communication style.",
    },
    {
      step: "02",
      title: "Project Assignment",
      desc: "You share briefs, we handle execution in the background.",
    },
    {
      step: "03",
      title: "Branded Delivery",
      desc: "We deliver code and assets under your agency's brand.",
    },
    {
      step: "04",
      title: "Ongoing Support",
      desc: "We provide long-term maintenance and scaling support.",
    },
  ];

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "white" }}>
      <Head>
        {" "}
        <title>
          Web Development & Automation for SMEs & Startups | LinXar Labs
        </title>
        <meta
          name="description"
          content="Affordable web development, automation, and IT support for SMEs and startups. LinXar Labs helps small businesses grow with scalable digital solutions."
        />
        <meta
          name="keywords"
          content="web development for startups, automation for SMEs, IT support for small businesses, affordable business automation, MVP web development"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "Web Development & Automation for SMEs & Startups",
              provider: {
                "@type": "Organization",
                name: "LinXar Labs",
                url: "https://linxarlabs.com",
              },
              areaServed: [
                { "@type": "Country", name: "United States" },
                { "@type": "Country", name: "United Kingdom" },
                { "@type": "Country", name: "Netherlands" },
                { "@type": "Country", name: "Canada" },
                { "@type": "Country", name: "Australia" },
              ],
              description:
                "LinXar Labs provides web development, workflow automation, and IT support tailored to SMEs and startups, helping them grow efficiently.",
            }),
          }}
        />
      </Head>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                lineHeight: 1.2,
                mb: 3,
                fontWeight: 700,
              }}
            >
              White-Label Web Development & Automation
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                lineHeight: 1.6,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Partner with LinXar Labs to scale your agency. We deliver seamless
              services under your brand so you can focus on clients and growth.
            </Typography>
            <PopoverForm
              triggerElement={
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: "white",
                    color: "primary.main",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: alpha("#fff", 0.9),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Partner With Us
                </Button>
              }
            />
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {benefits.map((item, idx) => (
              <Grid key={idx} size={{ xs: 12, md: 4 }}>
                <Card
                  elevation={1}
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Our White-Label Partnership Process
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            A seamless collaboration process designed for agency growth and
            client satisfaction
          </Typography>

          <Grid container spacing={4}>
            {processSteps.map((item, idx) => (
              <Grid key={idx} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <Typography
                    variant="h3"
                    color="primary"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ fontSize: "2.5rem" }}
                  >
                    {item.step}
                  </Typography>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography color="text.secondary">{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Study Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Case Study: Scaling a Creative Agency
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Client: Dutch Marketing Agency
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Challenge
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  A growing marketing agency needed to expand their digital
                  services but lacked the in-house development capacity to meet
                  client demands without increasing overhead.
                </Typography>

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Solution
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  We implemented a comprehensive white-label partnership,
                  delivering web development and automation solutions under the
                  agency&apos;s brand with strict NDA protection.
                </Typography>
              </Box>

              <Box
                sx={{
                  background: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, color: theme.palette.success.main }}
                >
                  Results
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      +40%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Client Base Growth
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      100%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Brand Consistency
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: "white",
                  p: 4,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Ready to Scale Your Agency?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                  Let&apos;s discuss how our white-label solutions can help you
                  grow your client base and revenue.
                </Typography>
                <PopoverForm
                  triggerElement={
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: "white",
                        color: theme.palette.primary.main,
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.9),
                        },
                      }}
                    >
                      Start Partnership
                    </Button>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 3,
            }}
          >
            Scale Your Agency with White-Label Solutions
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, lineHeight: 1.6 }}
          >
            Focus on client relationships and business growth while we handle
            the technical delivery under your brand.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <PopoverForm
              triggerElement={
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Partner With Us
                </Button>
              }
            />
            <Button
              variant="outlined"
              size="large"
              onClick={handleConsult}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Book Discovery Call
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
